import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArtifactStorageDisabledError,
  listArtifacts,
  type ArtifactListResponse,
  type CodebaseResponse,
} from '@/lib/api';
import {
  decodeArtifactFilters,
  encodeArtifactFilters,
  type ArtifactListFiltersUi,
} from '@/lib/artifact-filters';
import { ArtifactsTable } from './ArtifactsTable';
import { ArtifactsFiltersBar } from './ArtifactsFiltersBar';
import { StorageDisabledBanner } from './StorageDisabledBanner';
import { ArtifactsPagination } from './ArtifactsPagination';

interface ArtifactsListViewProps {
  codebase: CodebaseResponse;
}

export function ArtifactsListView({ codebase }: ArtifactsListViewProps): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => decodeArtifactFilters(searchParams), [searchParams]);

  const updateFilters = useCallback(
    (next: Partial<ArtifactListFiltersUi>) => {
      setSearchParams(encodeArtifactFilters({ ...filters, ...next }), { replace: true });
    },
    [filters, setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    // Sync input when URL changes externally (e.g. nav back/forward).
    setSearchInput(filters.search ?? '');
  }, [filters.search]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateFilters({ search: value.trim() === '' ? undefined : value.trim(), page: 0 });
      }, 300);
    },
    [updateFilters]
  );

  useEffect(() => {
    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const { data, isLoading, isError, error } = useQuery<ArtifactListResponse>({
    queryKey: ['artifacts', { codebaseId: codebase.id, filters }],
    queryFn: () =>
      listArtifacts(codebase.id, {
        type: filters.type,
        scope: filters.scope,
        status: filters.status,
        ownerId: filters.ownerId,
        search: filters.search,
        includeArchived: filters.includeArchived,
        // Fetch one extra row so pagination can detect "is there a next page?"
        // without a server-side total count. The list view renders only the first
        // `pageSize` rows; the extra row is signal-only.
        limit: Math.min(filters.pageSize + 1, 200),
        offset: filters.page * filters.pageSize,
      }),
    retry: (failureCount, err) =>
      !(err instanceof ArtifactStorageDisabledError) && failureCount < 2,
  });

  const visibleArtifacts = data?.artifacts.slice(0, filters.pageSize) ?? [];
  const hasNext = (data?.artifacts.length ?? 0) > filters.pageSize;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 pb-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-text-secondary" />
          <input
            type="search"
            value={searchInput}
            onChange={(e): void => {
              handleSearchChange(e.target.value);
            }}
            placeholder="Search title, summary, or document body…"
            className="w-full rounded-md border border-border bg-surface pl-8 pr-2 py-1 text-sm"
            aria-label="Search artifacts"
          />
        </div>
      </div>
      <ArtifactsFiltersBar filters={filters} onChange={updateFilters} />
      {isError && error instanceof ArtifactStorageDisabledError && <StorageDisabledBanner />}
      {isError && !(error instanceof ArtifactStorageDisabledError) && (
        <div className="rounded-md bg-error/15 p-3 text-sm text-error">{error.message}</div>
      )}
      {isLoading && <div className="text-sm text-text-secondary">Loading…</div>}
      {data && <ArtifactsTable artifacts={visibleArtifacts} />}
      {data && (
        <ArtifactsPagination
          page={filters.page}
          hasNext={hasNext}
          onPageChange={(nextPage): void => {
            updateFilters({ page: nextPage });
          }}
        />
      )}
    </div>
  );
}
